<!--
* A view that handles toggling between contents / baggage forms
* Handles route params to see if we are editing and already existing item
* and will pass this through to the relevant form if we are
*
* @param {string} arrayName - The array name the item currently being edited is in
* @param {number} index - The index of item
-->

<template>
  <div>
    <form class="add_damaged_item_form">
      <!-- Subtitle -->
      <div class="subtitle">{{ $t(`${pagei18nKey}.title`) }}</div>

      <!-- Baggage / contents toggle -->
      <div v-if="contentItemsEnabled" class="add_damaged_item_form_toggle">
        <div class="field">
          <label class="label">{{ $t(`${pagei18nKey}.itemTypeLabel`) }}</label>
          <ms-navigation-bar
            :buttons="[baggageButton, contentsButton]"
            @navigationButtonClick="setItemType"
          />
        </div>
      </div>

      <!-- Baggage damage form -->
      <baggage-item-form
        :currentItem="currentItem"
        :currentItemIndex="currentIndex"
        :addToClaim="addToClaim"
        :claimId="claimId"
        :imagesRequired="imagesRequired"
        :showImagesUpload="showImagesUpload"
        v-if="itemType === itemTypeEnum.BAGGAGE"
        @imageValidityCheck="imageValidityCheck"
        @purchaseDateChange="purchaseDateValidityCheck" />

      <!-- Contents damage form -->
      <contents-item-form
        :currentItem="currentItem"
        :currentItemIndex="currentIndex"
        :addToClaim="addToClaim"
        :claimId="claimId"
        v-if="itemType === itemTypeEnum.CONTENTS"
        @imageValidityCheck="imageValidityCheck"
        @purchaseDateChange="purchaseDateValidityCheck" />

    </form>

    <!-- Navigation buttons -->
    <div class="add_claims_item_button__container">
      <ms-navigation-bar
        :buttons="[backButton, addToClaimButton]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>

    <!-- Form validation pop up -->
    <ms-app-message
      v-if="showErrorModal"
      :title="$t(`message.formErrorTitle`)"
      :isOpen.sync="showErrorModal">
      <div>{{ $t(`message.formErrorMessage`) }}</div>
      <div v-if="imagesRequired && !imagesAreValid">{{ $t(`message.claimStatus.claimUpdateItemTitle`) }}</div>
    </ms-app-message>
  </div>
</template>
<script>
  import AppButton, {ButtonStates, ButtonTypes} from '@/components/app-button'
  import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
  import BaggageItemForm from '@/views/add-claims-item-view/baggage-item-form'
  import ContentsItemForm from '@/views/add-claims-item-view/contents-item-form'
  import themeSettings from '../../theme-settings.json'
  import MsAppMessage from '@/components/ms-app-message'

  // Baggage type references
  export const itemTypeEnum = {
    BAGGAGE: 'baggage',
    CONTENTS: 'contents'
  }

  // Array name references
  const arrayNameEnum = {
    BAGGAGE: 'baggageItems',
    CONTENTS: 'contentItems'
  }

  export default {
    name: 'AddAClaimsItemView',
    components: {
      AppButton,
      MsNavigationBar,
      BaggageItemForm,
      ContentsItemForm,
      MsAppMessage
    },

    data: function () {
      return {
        claimId: this.$store.getters.claimId,
        pagei18nKey: 'message.addClaimsItem',
        addToClaim: false,
        itemTypeEnum: itemTypeEnum,
        currentItem: null,
        itemType: itemTypeEnum.BAGGAGE,
        currentIndex: this.index && !isNaN(parseInt(this.index)) ? parseInt(this.index) : null,
        purchaseDateIsValid: false,
        contentItemsEnabled: themeSettings.theme.contentItemsEnabled,
        imagesAreValid: false,
        showErrorModal: false
      }
    },

    props: {
      arrayName: String,
      index: [Number, String]
    },

    /**
     * Check whether we have route props and if they match a given object in
     * item array then set relevant props to pass through to form components
     */
    beforeMount () {
      if (this.currentIndex !== null) {
        const array = this.$store.getters[this.arrayName]
        if (
          array !== undefined &&
          array[this.currentIndex] !== undefined
        ) {
          this.currentItem = array[this.currentIndex]
          this.itemType = this.arrayName === arrayNameEnum.BAGGAGE ? itemTypeEnum.BAGGAGE : itemTypeEnum.CONTENTS
        }
      }
    },

    computed: {
      /**
       * Get baggage button object
       */
      baggageButton: {
        get () {
          return {
            state: this.baggageButtonState,
            type: this.itemType === this.itemTypeEnum.BAGGAGE ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY,
            text: `${this.pagei18nKey}.baggageButton`,
            id: itemTypeEnum.BAGGAGE
          }
        }
      },
      /**
       * Returns true if theme settings says images are required and we are allowed
       * to display images uplaod controls
       */
      imagesRequired: {
        get () {
          return this.showImagesUpload && themeSettings.theme.imagesRequired
        }
      },

      /**
       * Returns flag true false informin us if we can show images upload
       * Flag is based on theme settings (imagesEnabled flag)
       * but also depends on user input and other flags (like imagesDisabledIfValidPir
       * which hides images upload if pir is provided by user)
       */
      showImagesUpload: {
        get () {
          // pir filled and images disabled if there's pir? return false
          // rpk: commented out per new requirements from Jo on 2nd Feb 2026
          // if (this.$store.getters.isPirPulledFromAirportReport &&
          // !!themeSettings.theme.imagesDisabledIfValidPir) {
          //   return false
          // }
          // else normal flow
          return themeSettings.theme.imagesEnabled
        }
      },

      /**
       * Get contents button object
       */
      contentsButton: {
        get () {
          return {
            state: this.contentsButtonState,
            type: this.itemType === this.itemTypeEnum.CONTENTS ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY,
            text: `${this.pagei18nKey}.contentsButton`,
            id: itemTypeEnum.CONTENTS
          }
        }
      },

      /**
       * Get baggage button state
       * we disable toggling to other item type if we're currently editing
       */
      baggageButtonState: {
        get () {
          if (
            this.arrayName === arrayNameEnum.BAGGAGE ||
            this.arrayName === undefined
          ) {
            return ButtonStates.ENABLED
          }
          return ButtonStates.DISABLED
        }
      },

      /**
       * Get contents button state
       * we disable toggling to other item type if we're currently editing
       */
      contentsButtonState: {
        get () {
          if (
            this.arrayName === arrayNameEnum.CONTENTS ||
            this.arrayName === undefined
          ) {
            return ButtonStates.ENABLED
          }
          return ButtonStates.DISABLED
        }
      },

      /**
       * Get back button object
       */
      backButton: {
        get () {
          let defaultBackButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.BACK))
          // we only enable the back button when adding a new item, not when we're editting an existing item
          // Note, for now i always leave the back button enabled as it's less confusing.
          return Object.assign(
            defaultBackButton,
            {
              state: this.currentItem ? ButtonStates.ENABLED : ButtonStates.ENABLED
            }
          )
        }
      },

      /**
       * Get add to claim button object
       */
      addToClaimButton: {
        get () {
          let defaultNextButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.NEXT))
          return Object.assign(
            defaultNextButton,
            {
              state: ButtonStates.ENABLED,
              text: this.currentItem ? `${this.pagei18nKey}.saveToClaimButton` : `${this.pagei18nKey}.addToClaimButton`
            }
          )
        }
      },

      /**
       * Checks each form field to see if the form is valid
       *
       * @returns {boolean} Whether form is valid
       */
      isFormValid () {
        return (
          Object.keys(this.fields).every(key => this.fields[key].valid) &&
          (this.imagesAreValid || !this.imagesRequired) && this.purchaseDateIsValid
        )
      }

    },

    methods: {
      /**
       * Set the item type
       *
       * @param {string} buttonId - Id of the relevant button
       */
      setItemType (buttonId) {
        this.itemType = buttonId
      },

      /**
       * On navigation button click
       *
       * @param {string} buttonId - Id of button
       */
      navigationButtonClick (buttonId) {
        if (buttonId === DefaultNavigationButtons.BACK.id) {
          this.$router.go(-1)
        } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
          // checks if form is valid before changing route
          this.$validator.validate().then(valid => {
            if (this.isFormValid) {
              this.addToClaim = true
            } else {
              this.showErrorModal = true
            }
          })
        }
      },

      /**
       * Check whether images are valid
       *
       * @param {boolean} value - Whether images are valid
       */
      imageValidityCheck (value) {
        this.imagesAreValid = value
      },

      /**
       * Checks whether purchase date is valid
       *
       * @param {string|number} value - Value of purchase date
       */
      purchaseDateValidityCheck (value) {
        this.purchaseDateIsValid = (value !== null)
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../../assets/sass/main.scss";

  .add_claims_item_button__container {
    @include mobile {
      padding: 0 $dims-horizontal-gutter;
    }
  }

  .add_damaged_item_form {
    @extend %app_form_container;
  }

  .new_image_upload_button {
    max-width: none;
    padding: $dims-spacing-small 0;
    margin-top: $dims-spacing-xlarge;
  }
</style>
